import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import {
  ResumePDFLink,
  ResumePDFText,
} from "components/Resume/ResumePDF/common";
import { handleClick } from "lib/utils/navigator";
import { useHighlight, shouldHighlightText, isHighlightTarget, getMergedHighlightStyle } from "components/Resume/HighlightContext";
import { useMistakeAnalysis, isSentenceMistake, isSingleWordMistake } from "components/Resume/MistakeAnalysisContext";

const MISTAKE_TEXT_COLOR = "#dc2626";


export const TemplateFifteenPDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) =>{
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  return  (
  <div
  onClick={handleClickCallback}
    style={{
      ...styles.flexCol,
      gap: spacing["2"],
      marginTop: spacing["5"],
      cursor:"pointer"
    }}
  >
    {heading && (
      <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
          <Text
          
          style={{
            fontWeight: "bold",
            letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
            color: themeColor,
          }}
          debug={DEBUG_RESUME_PDF_FLAG}
        >
          {heading}
        </Text>
        {themeColor && (
          <View
            style={{
              height: "2pt",
              width: "20%",
              backgroundColor: themeColor,
              marginRight: spacing["3.5"],
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          />
        )}
      </View>
    )}
    {children}
  </div>
)};

export const ResumePDFDescriptionWithWhiteFont = ({
  items,
  enableHighlight = false,
  enableMistakeHighlight = false,
  section,
  itemIndex,
  themeColor,
}: {
  items: string[];
  enableHighlight?: boolean;
  enableMistakeHighlight?: boolean;
  section?: string;
  itemIndex?: number;
  themeColor?: string;
}) => {
  const highlight = useHighlight();
  const mistakeAnalysis = useMistakeAnalysis();
  
  // Check if items is defined and has at least one element
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>
          {(enableHighlight || enableMistakeHighlight)
            ? renderNodeWithHighlight(node, section, itemIndex, idx, themeColor, highlight, enableMistakeHighlight, mistakeAnalysis)
            : renderNode(node)}
        </React.Fragment>
      ))}
    </View>
  );
};

const parseAndTransformHTML = (htmlString: string): React.ReactNode[] => {
  // Create a temporary DOM element to parse the HTML string
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  // Recursive function to process and transform HTML nodes
  const transformNode = (node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;

      switch (element.nodeName.toLowerCase()) {
        case "strong":
          return (
            <ResumePDFText key={Math.random()} bold={true} themeColor="white">
              {element.textContent}
            </ResumePDFText>
          );
        case "em":
          return (
            <ResumePDFText key={Math.random()} italic={true} themeColor="white">
              {element.textContent}
            </ResumePDFText>
          );
        case "u":
          return (
            <ResumePDFText
              key={Math.random()}
              underline={true}
              themeColor="white"
            >
              {element.textContent}
            </ResumePDFText>
          );
        case "a":
          const href = element.getAttribute("href");
          return (
            <ResumePDFLink
              isPDF={DEBUG_RESUME_PDF_FLAG as true}
              src={href || "#"}
              key={Math.random()}
            >
              <ResumePDFText themeColor="blue" underline={true}>
                {element.textContent}
              </ResumePDFText>
            </ResumePDFLink>
          );
        case "span":
          // Skip span elements
          return null;
        case "p":
          return (
            <View key={Math.random()}>
              <ResumePDFText>
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
              ;
            </View>
          );
        case "li":
          return (
            <View key={Math.random()} style={{ flexDirection: "row" }}>
              <ResumePDFText
                style={{
                  paddingLeft: 4, // adjust spacing as needed
                  paddingRight: 4, // adjust spacing as needed
                  lineHeight: 1.3,
                }}
                bold={true}
              >
                {"•"}
              </ResumePDFText>
              <ResumePDFText
                style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
              >
                {Array.from(element.childNodes).map(transformNode)}
              </ResumePDFText>
            </View>
          );
        case "ol":
          return (
            <View key={Math.random()} style={{ flexDirection: "column" }}>
              {Array.from(element.childNodes).map((child, index) =>
                transformNode(child)
              )}
            </View>
          );
        default:
          return null;
      }
    }

    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }

    return null;
  };

  Array.from(doc.body.childNodes).forEach((child) => {
    const transformedNode = transformNode(child);
    if (transformedNode) {
      transformedChildren.push(transformedNode);
    }
  });

  return transformedChildren;
};

const renderNode = (node: Node, themeColor: string = "black"): React.ReactNode => {
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    switch (element.nodeName.toLowerCase()) {
      case "p":
        return (
          <View>
            <ResumePDFText
              style={{ lineHeight: 1.3, flexGrow: 1 }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
              <br />
            </ResumePDFText>
          </View>
        );
        
      case "ol":
        return (
          <View style={{ ...styles.flexCol }}>
            {Array.from(element.childNodes).map((child, index) =>
              child.nodeName.toLowerCase() === "li" ? (
                <View key={index} style={{ ...styles.flexRow }}>
                  <ResumePDFText
                    style={{
                      paddingLeft: spacing["2"],
                      paddingRight: spacing["2"],
                      lineHeight: 1.3,
                    }}
                    bold={true}
                    themeColor={themeColor}
                  >
                    {"•"}
                  </ResumePDFText>
                  <ResumePDFText
                    style={{ lineHeight: 1.3, flexGrow: 1, flexBasis: 0 }}
                    themeColor={themeColor}
                  >
                    {parseAndTransformHTML((child as Element).innerHTML)}
                  </ResumePDFText>
                </View>
              ) : null
            )}
          </View>
        );
        
      case "li":
        return (
          <View style={{ ...styles.flexRow }}>
            <ResumePDFText
              style={{
                paddingLeft: spacing["2"],
                paddingRight: spacing["2"],
                lineHeight: 1.3,
              }}
              bold={true}
              themeColor={themeColor}
            >
              {"•"}
            </ResumePDFText>
            <ResumePDFText
              style={{
                lineHeight: 1.3,
                flexGrow: 1,
                flexBasis: 0,
              }}
              themeColor={themeColor}
            >
              {parseAndTransformHTML(element.innerHTML)}
            </ResumePDFText>
          </View>
        );
        
      default:
        return null;
    }
  }
  return null;
};


export const extractNodesFromHtml = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const nodes = Array.from(doc.body.childNodes);
  return nodes;
};

// Highlighting functions
const renderNodeWithHighlight = (
  node: Node,
  section?: string,
  itemIndex?: number,
  descIndex?: number,
  themeColor?: string,
  highlight?: ReturnType<typeof useHighlight>,
  enableMistakeHighlight?: boolean,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode => {
  const highlightMode = highlight?.mode;
  const highlightedWords = highlight?.texts || [];
  const highlightedSentences = highlight?.texts || [];
  const isTarget = !!(section && itemIndex !== undefined && highlight && isHighlightTarget(section, itemIndex, highlight));

  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent || "";
    
    // Check for sentence-level mistakes
    const isSentenceMistakeMatch = !!(enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && section && itemIndex !== null && itemIndex !== undefined &&
      isSentenceMistake(text, section, itemIndex ?? null, mistakeAnalysis.mistakeData));
    
    // Priority 1: Sentence mistakes (show red text regardless of hover)
    if (isSentenceMistakeMatch) {
      if (highlightMode === "word") {
        // Sentence mistake + word hover: render with word highlights but force red for sentence
        return renderTextWithWordHighlights(text, section, itemIndex, descIndex, themeColor, false, false, false, enableMistakeHighlight, mistakeAnalysis, highlight, isTarget, true);
      } else if (highlightMode === "sentence" && shouldHighlightText(text, highlightedSentences, highlightMode)) {
        // Sentence mistake + sentence hover: yellow background + red text
        const baseStyle = { color: themeColor || "white" };
        const mergedStyle = highlight ? getMergedHighlightStyle(baseStyle, highlight.style, isTarget) : baseStyle;
        return <Text style={{ ...mergedStyle, color: MISTAKE_TEXT_COLOR }}>{text}</Text>;
      } else {
        // Sentence mistake only: red text
        return <Text style={{ color: MISTAKE_TEXT_COLOR }}>{text}</Text>;
      }
    }
    
    // Priority 2: Hover highlighting
    if (highlightMode === "word") {
      return renderTextWithWordHighlights(text, section, itemIndex, descIndex, themeColor, false, false, false, enableMistakeHighlight, mistakeAnalysis, highlight, isTarget, false);
    } else if (
      highlightMode === "sentence" &&
      shouldHighlightText(text, highlightedSentences, highlightMode)
    ) {
      const baseStyle = { color: themeColor || "white" };
      const mergedStyle = highlight ? getMergedHighlightStyle(baseStyle, highlight.style, isTarget) : baseStyle;
      return <Text style={mergedStyle}>{text}</Text>;
    }
    
    // Priority 3: Word-level mistakes (when no hover)
    if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && section && itemIndex !== null && itemIndex !== undefined) {
      return renderTextWithWordHighlights(text, section, itemIndex, descIndex, themeColor, false, false, false, enableMistakeHighlight, mistakeAnalysis, highlight, false, false);
    }
    
    return <Text style={{ color: themeColor || "white" }}>{text}</Text>;
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as HTMLElement;
    const childText = element.textContent || "";
    
    // Check for sentence-level mistakes
    const isSentenceMistakeMatch = !!(enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && section && itemIndex !== null && itemIndex !== undefined &&
      isSentenceMistake(childText, section, itemIndex ?? null, mistakeAnalysis.mistakeData));

    switch (element.nodeName.toLowerCase()) {
      case "strong":
        // Priority 1: Sentence mistakes
        if (isSentenceMistakeMatch) {
          if (highlightMode === "word") {
            return renderTextWithWordHighlights(childText, section, itemIndex, descIndex, themeColor, true, false, false, enableMistakeHighlight, mistakeAnalysis, highlight, isTarget, true);
          } else if (highlightMode === "sentence" && shouldHighlightText(childText, highlightedSentences, highlightMode)) {
            const baseStyle = { color: themeColor || "white", fontFamily: "Lato Bold" };
            const mergedStyle = highlight ? getMergedHighlightStyle(baseStyle, highlight.style, isTarget) : baseStyle;
            return <Text style={{ ...mergedStyle, color: MISTAKE_TEXT_COLOR }}>{childText}</Text>;
          } else {
            return <Text style={{ color: MISTAKE_TEXT_COLOR, fontFamily: "Lato Bold" }}>{childText}</Text>;
          }
        }
        // Priority 2: Hover highlighting
        if (highlightMode === "word") {
          return renderTextWithWordHighlights(childText, section, itemIndex, descIndex, themeColor, true, false, false, enableMistakeHighlight, mistakeAnalysis, highlight, isTarget, false);
        } else if (
          highlightMode === "sentence" &&
          shouldHighlightText(childText, highlightedSentences, highlightMode)
        ) {
          const baseStyle = { color: themeColor || "white", fontFamily: "Lato Bold" };
          const mergedStyle = highlight ? getMergedHighlightStyle(baseStyle, highlight.style, isTarget) : baseStyle;
          return <Text style={mergedStyle}>{childText}</Text>;
        }
        // Priority 3: Word-level mistakes
        if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && section && itemIndex !== null && itemIndex !== undefined) {
          return renderTextWithWordHighlights(childText, section, itemIndex, descIndex, themeColor, true, false, false, enableMistakeHighlight, mistakeAnalysis, highlight, false, false);
        }
        return <ResumePDFText bold={true} themeColor={themeColor || "white"}>{childText}</ResumePDFText>;

      case "em":
        // Priority 1: Sentence mistakes
        if (isSentenceMistakeMatch) {
          if (highlightMode === "word") {
            return renderTextWithWordHighlights(childText, section, itemIndex, descIndex, themeColor, false, true, false, enableMistakeHighlight, mistakeAnalysis, highlight, isTarget, true);
          } else if (highlightMode === "sentence" && shouldHighlightText(childText, highlightedSentences, highlightMode)) {
            const baseStyle = { color: themeColor || "white", fontFamily: "Lato Italic" };
            const mergedStyle = highlight ? getMergedHighlightStyle(baseStyle, highlight.style, isTarget) : baseStyle;
            return <Text style={{ ...mergedStyle, color: MISTAKE_TEXT_COLOR }}>{childText}</Text>;
          } else {
            return <Text style={{ color: MISTAKE_TEXT_COLOR, fontFamily: "Lato Italic" }}>{childText}</Text>;
          }
        }
        // Priority 2: Hover highlighting
        if (highlightMode === "word") {
          return renderTextWithWordHighlights(childText, section, itemIndex, descIndex, themeColor, false, true, false, enableMistakeHighlight, mistakeAnalysis, highlight, isTarget, false);
        } else if (
          highlightMode === "sentence" &&
          shouldHighlightText(childText, highlightedSentences, highlightMode)
        ) {
          const baseStyle = { color: themeColor || "white", fontFamily: "Lato Italic" };
          const mergedStyle = highlight ? getMergedHighlightStyle(baseStyle, highlight.style, isTarget) : baseStyle;
          return <Text style={mergedStyle}>{childText}</Text>;
        }
        // Priority 3: Word-level mistakes
        if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && section && itemIndex !== null && itemIndex !== undefined) {
          return renderTextWithWordHighlights(childText, section, itemIndex, descIndex, themeColor, false, true, false, enableMistakeHighlight, mistakeAnalysis, highlight, false, false);
        }
        return <ResumePDFText italic={true} themeColor={themeColor || "white"}>{childText}</ResumePDFText>;

      case "u":
        // Priority 1: Sentence mistakes
        if (isSentenceMistakeMatch) {
          if (highlightMode === "word") {
            return renderTextWithWordHighlights(childText, section, itemIndex, descIndex, themeColor, false, false, true, enableMistakeHighlight, mistakeAnalysis, highlight, isTarget, true);
          } else if (highlightMode === "sentence" && shouldHighlightText(childText, highlightedSentences, highlightMode)) {
            const baseStyle = { color: themeColor || "white", textDecoration: "underline" };
            const mergedStyle = highlight ? getMergedHighlightStyle(baseStyle, highlight.style, isTarget) : baseStyle;
            return <Text style={{ ...mergedStyle, color: MISTAKE_TEXT_COLOR }}>{childText}</Text>;
          } else {
            return <Text style={{ color: MISTAKE_TEXT_COLOR, textDecoration: "underline" }}>{childText}</Text>;
          }
        }
        // Priority 2: Hover highlighting
        if (highlightMode === "word") {
          return renderTextWithWordHighlights(childText, section, itemIndex, descIndex, themeColor, false, false, true, enableMistakeHighlight, mistakeAnalysis, highlight, isTarget, false);
        } else if (
          highlightMode === "sentence" &&
          shouldHighlightText(childText, highlightedSentences, highlightMode)
        ) {
          const baseStyle = { color: themeColor || "white", textDecoration: "underline" };
          const mergedStyle = highlight ? getMergedHighlightStyle(baseStyle, highlight.style, isTarget) : baseStyle;
          return <Text style={mergedStyle}>{childText}</Text>;
        }
        // Priority 3: Word-level mistakes
        if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && section && itemIndex !== null && itemIndex !== undefined) {
          return renderTextWithWordHighlights(childText, section, itemIndex, descIndex, themeColor, false, false, true, enableMistakeHighlight, mistakeAnalysis, highlight, false, false);
        }
        return <ResumePDFText underline={true} themeColor={themeColor || "white"}>{childText}</ResumePDFText>;

      case "a":
        const href = element.getAttribute("href");
        return (
          <ResumePDFLink isPDF={DEBUG_RESUME_PDF_FLAG as true} src={href || "#"}>
            <ResumePDFText themeColor="blue" underline={true}>{childText}</ResumePDFText>
          </ResumePDFLink>
        );

      case "li":
        return (
          <View style={{ ...styles.flexRow, marginTop: spacing["0.5"] }}>
            <Text style={{ width: 12, color: themeColor || "white" }}>•</Text>
            <View style={{ flex: 1 }}>
              {Array.from(element.childNodes).map((child, i) => (
                <React.Fragment key={i}>
                  {renderNodeWithHighlight(child, section, itemIndex, descIndex, themeColor, highlight, enableMistakeHighlight, mistakeAnalysis)}
                </React.Fragment>
              ))}
            </View>
          </View>
        );

      case "ul":
        return (
          <View>
            {Array.from(element.childNodes).map((child, i) => (
              <React.Fragment key={i}>
                {renderNodeWithHighlight(child, section, itemIndex, descIndex, themeColor, highlight, enableMistakeHighlight, mistakeAnalysis)}
              </React.Fragment>
            ))}
          </View>
        );

      default:
        return (
          <>
            {Array.from(element.childNodes).map((child, i) => (
              <React.Fragment key={i}>
                {renderNodeWithHighlight(child, section, itemIndex, descIndex, themeColor, highlight, enableMistakeHighlight, mistakeAnalysis)}
              </React.Fragment>
            ))}
          </>
        );
    }
  }

  return null;
};

const parseAndTransformHTMLWithHighlight = (
  htmlString: string,
  section?: string,
  itemIndex?: number,
  descIndex?: number,
  themeColor?: string,
  highlight?: ReturnType<typeof useHighlight>,
  enableMistakeHighlight?: boolean,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>
): React.ReactNode[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const transformedChildren: React.ReactNode[] = [];

  Array.from(doc.body.childNodes).forEach((node, idx) => {
    transformedChildren.push(
      <React.Fragment key={idx}>
        {renderNodeWithHighlight(node, section, itemIndex, descIndex, themeColor, highlight, enableMistakeHighlight, mistakeAnalysis)}
      </React.Fragment>
    );
  });

  return transformedChildren;
};

const renderTextWithWordHighlights = (
  text: string,
  section?: string,
  itemIndex?: number,
  descIndex?: number,
  themeColor?: string,
  bold = false,
  italic = false,
  underline = false,
  enableMistakeHighlight?: boolean,
  mistakeAnalysis?: ReturnType<typeof useMistakeAnalysis>,
  highlight?: ReturnType<typeof useHighlight>,
  isHoverTarget: boolean = false,
  forceRedText: boolean = false
): React.ReactNode => {
  const highlightedWords = highlight?.texts || [];;

  if (!highlightedWords || highlightedWords.length === 0) {
    // No hover highlights, check for mistakes only
    if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && section && itemIndex !== null && itemIndex !== undefined) {
      const textWords = text.split(/\s+/).filter(Boolean);
      const hasAnyMistake = textWords.some(word =>
        isSingleWordMistake(word, section, itemIndex ?? null, mistakeAnalysis.mistakeData!)
      );

      if (hasAnyMistake) {
        return (
          <Text>
            {textWords.map((word, i) => {
              const isMistake = isSingleWordMistake(word, section, itemIndex ?? null, mistakeAnalysis.mistakeData!);
              const baseStyle: any = { color: isMistake ? MISTAKE_TEXT_COLOR : (themeColor || "white") };
              if (bold) baseStyle.fontFamily = "Lato Bold";
              if (italic) baseStyle.fontFamily = "Lato Italic";
              if (underline) baseStyle.textDecoration = "underline";
              
              return (
                <React.Fragment key={i}>
                  {i > 0 && " "}
                  <Text style={baseStyle}>{word}</Text>
                </React.Fragment>
              );
            })}
          </Text>
        );
      }
    }
    
    const baseStyle: any = { color: themeColor || "white" };
    if (bold) baseStyle.fontFamily = "Lato Bold";
    if (italic) baseStyle.fontFamily = "Lato Italic";
    if (underline) baseStyle.textDecoration = "underline";
    return <Text style={baseStyle}>{text}</Text>;
  }

  const pattern = new RegExp(
    `\\b(${highlightedWords.map((w: string) => w.replace(/[.*+?^${}()|[\\]\\]/g, "\\$&")).join("|")})\\b`,
    "giu"
  );

  // STEP 1: Split by hover words while tracking matches
  const parts: Array<{ text: string; isHoverMatch: boolean }> = [];
  let lastIndex = 0;
  let match;
  
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.substring(lastIndex, match.index), isHoverMatch: false });
    }
    parts.push({ text: match[0], isHoverMatch: true });
    lastIndex = pattern.lastIndex;
  }
  
  if (lastIndex < text.length) {
    parts.push({ text: text.substring(lastIndex), isHoverMatch: false });
  }
  
  if (parts.length === 0) {
    parts.push({ text, isHoverMatch: false });
  }
  
  // STEP 2: For each part, check for mistakes
  const finalParts: Array<{ text: string; isHoverMatch: boolean; isMistake: boolean }> = [];
  
  for (const part of parts) {
    if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && section && itemIndex !== null && itemIndex !== undefined && !part.isHoverMatch && part.text.trim()) {
      // Non-hover part: split by whitespace to check each word
      const wordParts = part.text.split(/(\s+)/);
      
      for (const wordPart of wordParts) {
        if (!wordPart) continue;
        
        const trimmed = wordPart.trim();
        const isMistake = trimmed.length > 0 && isSingleWordMistake(
          trimmed,
          section,
          itemIndex ?? null,
          mistakeAnalysis.mistakeData
        );
        
        finalParts.push({
          text: wordPart,
          isHoverMatch: false,
          isMistake
        });
      }
    } else if (enableMistakeHighlight && mistakeAnalysis?.enabled && mistakeAnalysis?.mistakeData && section && itemIndex !== null && itemIndex !== undefined && part.isHoverMatch) {
      // Hover match: check if this word is also a mistake
      const isMistake = isSingleWordMistake(
        part.text.trim(),
        section,
        itemIndex ?? null,
        mistakeAnalysis.mistakeData
      );
      
      finalParts.push({
        text: part.text,
        isHoverMatch: true,
        isMistake
      });
    } else {
      finalParts.push({
        text: part.text,
        isHoverMatch: part.isHoverMatch,
        isMistake: false
      });
    }
  }

  return (
    <Text>
      {finalParts.map((part, i) => {
        const baseStyle: any = { color: themeColor || "white" };
        if (bold) baseStyle.fontFamily = "Lato Bold";
        if (italic) baseStyle.fontFamily = "Lato Italic";
        if (underline) baseStyle.textDecoration = "underline";

        const isRed = forceRedText || part.isMistake;
        
        if (part.isHoverMatch && isHoverTarget && highlight) {
          // Hover match: apply yellow background
          const mergedStyle = getMergedHighlightStyle(baseStyle, highlight.style, true);
          if (isRed) {
            mergedStyle.color = MISTAKE_TEXT_COLOR;
          }
          return (
            <Text key={i} style={mergedStyle}>
              {part.text}
            </Text>
          );
        } else if (isRed) {
          // Mistake only: red text
          return (
            <Text key={i} style={{ ...baseStyle, color: MISTAKE_TEXT_COLOR }}>
              {part.text}
            </Text>
          );
        } else {
          // Normal text
          return (
            <Text key={i} style={baseStyle}>
              {part.text}
            </Text>
          );
        }
      })}
    </Text>
  );
};

export const ResumePDFDescription = ({ 
  items,
  themeColor,
  enableHighlight = false,
  enableMistakeHighlight = false,
  section,
  itemIndex,
}: { 
  items: string[],
  themeColor?:string,
  enableHighlight?: boolean,
  enableMistakeHighlight?: boolean,
  section?: string,
  itemIndex?: number,
}) => {
  const highlight = useHighlight();
  const mistakeAnalysis = useMistakeAnalysis();
  
  if (!items || items.length === 0) {
    return null; // or render a placeholder, like <p>No descriptions available</p>
  }
  const newDescription = items.join("");
  const descriptionNodes = extractNodesFromHtml(newDescription);

  return (
    <View wrap={false}>
      {descriptionNodes.map((node, idx) => (
        <React.Fragment key={idx}>
          {(enableHighlight || enableMistakeHighlight)
            ? renderNodeWithHighlight(node, section, itemIndex, idx, themeColor, highlight, enableMistakeHighlight, mistakeAnalysis)
            : renderNode(node, themeColor)}
        </React.Fragment>
      ))}
    </View>
  );
};

// Export optimized section components
export { TemplateFifteenSectionWithSubsections, TemplateFifteenSection } from "./sections";
